/*
Purpose: Create a student pause from the Calendar (v2) session drawer.
*/

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import { Form, Modal } from 'react-bootstrap';
import Button from '@mui/material/Button';

import { PAUSE_APPLY_TYPE } from '../constants';

const EMPTY_FORM = {
  pauseStartDate: '',
  pauseEndDate: '',
  pauseDescription: '',
  pauseApplyType: PAUSE_APPLY_TYPE.ALL,
};

const CONFIRM_MESSAGE =
  "If any sessions in this pause period have already been paid, the corresponding amount will be transferred to the student's wallet. Once the pause start date is reached, this pause cannot be edited or deleted. Do you want to continue?";

function validateForm(form) {
  const errors = {};
  const today = Moment().startOf('day');
  const maxDate = Moment().add(1, 'year').endOf('day');

  if (!form.pauseStartDate) {
    errors.pauseStartDate = 'Start date is required.';
  } else {
    const start = Moment(form.pauseStartDate, 'YYYY-MM-DD', true);
    if (!start.isValid()) {
      errors.pauseStartDate = 'Please enter a valid date.';
    } else if (start.isBefore(today, 'day') || start.isAfter(maxDate, 'day')) {
      errors.pauseStartDate = 'Start date must be within the next 1 year.';
    }
  }

  if (!form.pauseEndDate) {
    errors.pauseEndDate = 'End date is required.';
  } else {
    const end = Moment(form.pauseEndDate, 'YYYY-MM-DD', true);
    if (!end.isValid()) {
      errors.pauseEndDate = 'Please enter a valid date.';
    } else if (end.isBefore(today, 'day') || end.isAfter(maxDate, 'day')) {
      errors.pauseEndDate = 'End date must be within the next 1 year.';
    } else if (form.pauseStartDate) {
      const start = Moment(form.pauseStartDate, 'YYYY-MM-DD', true);
      if (start.isValid() && end.isBefore(start, 'day')) {
        errors.pauseEndDate = 'End date must be on/after start date.';
      }
    }
  }

  if (!form.pauseDescription.trim()) {
    errors.pauseDescription = 'Please enter a description.';
  }

  if (
    form.pauseApplyType !== PAUSE_APPLY_TYPE.ALL &&
    form.pauseApplyType !== PAUSE_APPLY_TYPE.THIS_SESSION
  ) {
    errors.pauseApplyType = 'Please select an option.';
  }

  return errors;
}

/**
 * @param {Object} props
 * @param {boolean} props.show
 * @param {Object|null} props.student Student view model from the drawer.
 * @param {string} props.sessionTemplateName Label for the "this session only" option.
 * @param {boolean} props.isSubmitting
 * @param {Function} props.onCancel
 * @param {Function} props.onSave Called with the pause form payload after confirm.
 */
export default function PauseStudentModal({
  show,
  student,
  sessionTemplateName,
  isSubmitting,
  onCancel,
  onSave,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (show) {
      setForm(EMPTY_FORM);
      setErrors({});
      setShowConfirm(false);
    }
  }, [show, student]);

  function updateField(field, value) {
    setForm((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => {
      if (!previous[field]) {
        return previous;
      }
      const next = { ...previous };
      delete next[field];
      return next;
    });
  }

  function handleSubmit(event) {
    event.preventDefault();
    const nextErrors = validateForm(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    setShowConfirm(true);
  }

  function handleConfirm() {
    onSave({
      pauseStartDate: form.pauseStartDate,
      pauseEndDate: form.pauseEndDate,
      pauseDescription: form.pauseDescription.trim(),
      pauseApplyType: form.pauseApplyType,
    });
  }

  function handleCancel() {
    setShowConfirm(false);
    onCancel();
  }

  const sessionLabel = sessionTemplateName
    ? `Apply to this session only (${sessionTemplateName})`
    : 'Apply to this session only';

  return (
    <>
      <Modal show={show && !showConfirm} onHide={handleCancel} centered size="lg">
        <Form autoComplete="off" onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title as="h5">
              Pause Session
              {student ? ` — ${student.name}` : ''}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6">
                <h6 className="form-lable required-field">Start Date</h6>
                <Form.Control
                  type="date"
                  value={form.pauseStartDate}
                  min={Moment().format('YYYY-MM-DD')}
                  max={
                    form.pauseEndDate ||
                    Moment().add(1, 'year').format('YYYY-MM-DD')
                  }
                  onChange={(event) => updateField('pauseStartDate', event.target.value)}
                  className={errors.pauseStartDate ? 'input-error' : ''}
                  disabled={isSubmitting}
                />
                {errors.pauseStartDate ? (
                  <span className="error">{errors.pauseStartDate}</span>
                ) : null}
              </div>

              <div className="col-md-6">
                <h6 className="form-lable required-field">End Date</h6>
                <Form.Control
                  type="date"
                  value={form.pauseEndDate}
                  min={form.pauseStartDate || Moment().format('YYYY-MM-DD')}
                  max={Moment().add(1, 'year').format('YYYY-MM-DD')}
                  onChange={(event) => updateField('pauseEndDate', event.target.value)}
                  className={errors.pauseEndDate ? 'input-error' : ''}
                  disabled={isSubmitting}
                />
                {errors.pauseEndDate ? (
                  <span className="error">{errors.pauseEndDate}</span>
                ) : null}
              </div>

              <div className="col-12">
                <h6 className="form-lable required-field">Description</h6>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={form.pauseDescription}
                  onChange={(event) => updateField('pauseDescription', event.target.value)}
                  className={errors.pauseDescription ? 'input-error mb-0' : ''}
                  disabled={isSubmitting}
                />
                {errors.pauseDescription ? (
                  <span className="error">{errors.pauseDescription}</span>
                ) : null}
              </div>

              <div className="col-12">
                <h6 className="form-lable required-field">Apply To</h6>
                <Form.Check
                  type="radio"
                  id="cv2-pause-apply-all"
                  label="Apply to all sessions"
                  checked={form.pauseApplyType === PAUSE_APPLY_TYPE.ALL}
                  onChange={() => updateField('pauseApplyType', PAUSE_APPLY_TYPE.ALL)}
                  disabled={isSubmitting}
                />
                <Form.Check
                  type="radio"
                  id="cv2-pause-apply-session"
                  label={sessionLabel}
                  checked={form.pauseApplyType === PAUSE_APPLY_TYPE.THIS_SESSION}
                  onChange={() => updateField('pauseApplyType', PAUSE_APPLY_TYPE.THIS_SESSION)}
                  disabled={isSubmitting}
                />
                {errors.pauseApplyType ? (
                  <div className="error">{errors.pauseApplyType}</div>
                ) : null}
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer className="pt-0">
            <div className="mx-auto d-flex gap-2">
              <Button
                variant="contained"
                className="primary-gray"
                type="button"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className="primary-filled"
                type="submit"
                disabled={isSubmitting}
              >
                Yes, Pause
              </Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={show && showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Confirm Pause</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0 required-field">{CONFIRM_MESSAGE}</p>
        </Modal.Body>
        <Modal.Footer className="pt-0">
          <div className="mx-auto d-flex gap-2">
            <Button
              variant="contained"
              className="primary-gray"
              type="button"
              onClick={() => setShowConfirm(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className="primary-filled"
              type="button"
              onClick={handleConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving…' : 'Yes'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}

PauseStudentModal.propTypes = {
  show: PropTypes.bool.isRequired,
  student: PropTypes.shape({
    name: PropTypes.string,
  }),
  sessionTemplateName: PropTypes.string,
  isSubmitting: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

PauseStudentModal.defaultProps = {
  student: null,
  sessionTemplateName: '',
  isSubmitting: false,
};

